import { Card } from "@/components/ui/card"
import ReportForm from "./ReportForm"
import ReportPlayer from "./ReportPlayer"
import { useOrder } from "../../../contexts/OrderContext"
import React from "react"
import { supabase } from "@/lib/supabase"
import { ArrowLeftIcon, ArrowRightIcon, CircleCheck, CircleCheckIcon, DownloadIcon, FileCheck2Icon, FileIcon, FileStackIcon, FileTextIcon, FlipHorizontalIcon, ForwardIcon, LightbulbIcon, LoaderCircleIcon, PencilIcon, UploadIcon } from "lucide-react"
import { OrderLoader, OrderPager } from "../Order"
import { Button } from "@/components/ui/button"
import { Navigate, useNavigate } from "react-router-dom"
import { useAuth } from "@/contexts/AuthContext"
import { format } from 'date-fns'
import { fr, is } from 'date-fns/locale'
import { useDocuments } from "@/contexts/DocumentsContext"
import Complete from "../Complete"
import { useAudio } from "@/contexts/AudioContext"
import { Checkbox } from "@/components/ui/checkbox"
import { AutoResizingTextarea } from "../mandate/MandateForm"
import { Label } from "@/components/ui/label"
import { Separator } from "@/components/ui/separator"
import PDF from "../PDF"
import { useTimer } from "@/hooks/timer"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { cn } from "@/lib/utils"
import { ScrollArea } from "@/components/ui/scroll-area"
import Guide from "@/Guide"

const { APP_API_URL } = import.meta.env

export default function Submission() {
    const { order, setOrder } = useOrder()
    const { setDocument } = useDocuments()
    const [loading, setLoading] = React.useState(false)
    const navigate = useNavigate()
    const { session, user } = useAuth()
    const { playerNotification } = useAudio()


    if (order.isGenerated && ((order.isLayout && !order.mandate.reviewed) || (!order.isLayout && (!order.is_sections_reviewed || !order.supported)))) {
        return <Navigate to={`/orders/${order.pydio_id}/report`} />
    }

    React.useEffect(() => {
        setDocument('report')
    }, []);

    React.useEffect(() => {
        if (!order || !!order.report_pspdfkit) return

        const intervalId = setInterval(async () => {
            try {
                const { data, error } = await supabase
                    .from('works')
                    .select('report_pspdfkit')
                    .eq('id', order.work.id)
                    .single()

                console.log('Fetching report...', order.pydio_id)

                if (data?.report_pspdfkit) {
                    setOrder((prevOrder) => ({
                        ...prevOrder,
                        report_pspdfkit: data.report_pspdfkit,
                    }))
                    playerNotification.current.play()
                    clearInterval(intervalId)
                }
            } catch (error) {
                console.error('Error fetching report:', error)
            }
        }, 5000)

        return () => clearInterval(intervalId)
    }, [order?.is_sections_reviewed, order?.pydio_id])

    React.useEffect(() => {
        if (!order || !!order.report_path) return

        const intervalId = setInterval(async () => {
            try {
                const { data, error } = await supabase
                    .from('works')
                    .select('report_path')
                    .eq('id', order.work.id)
                    .single()

                console.log('Fetching report...', order.pydio_id)

                if (data?.report_path) {
                    setOrder((prevOrder) => ({
                        ...prevOrder,
                        report_path: data.report_path,
                    }))
                    playerNotification.current.play()
                    clearInterval(intervalId)
                }
            } catch (error) {
                console.error('Error fetching report:', error)
            }
        }, 5000)

        return () => clearInterval(intervalId)
    }, [order?.is_sections_reviewed, order?.pydio_id])

    const download = async (path) => {
        setLoading(true)

        const { data: { signedUrl } } = await supabase.storage.from("folders").createSignedUrl(
            path,
            60
        )

        setLoading(false)

        window.open(signedUrl, '_blank')
    };

    const isAdmin = user?.settings?.isAdmin

    return <>
        <Card className="h-full w-full flex-grow min-h-0 overflow-auto flex-col gap-4 flex">
            {!order.finished_at && <Guide step="validation" />}
            <div className="h-full w-full flex-grow min-h-0 overflow-auto flex-col gap-4 flex p-4 pt-8">
                {(!order.isGenerated || order.finished_at) && <Complete>
                    {isAdmin && <div className="flex flex-col gap-2">
                        {order.report_path && <Button onClick={() => download(order.report_path)} variant={"ghost"}><FileIcon /> Télécharger le rapport</Button>}
                        {order.folder.compare_path && <Button onClick={() => download(order.folder.compare_path)} variant={"ghost"}><FlipHorizontalIcon /> Télécharger la comparaison du rapport</Button>}
                    </div>}
                </Complete>}

                {(order.isGenerated && !order.finished_at) && <SubmissionForm />}
            </div>
        </Card>
    </>
}

function SubmissionForm() {
    const [report, setReport] = React.useState(false)
    const [isGeneratedOk, setIsGeneratedOk] = React.useState(null)
    const [loading, setLoading] = React.useState(false)
    const [loadingFinish, setLoadingFinish] = React.useState(false)
    const [comment, setComment] = React.useState("")
    const { order, setOrder, activeTime } = useOrder()
    const { session } = useAuth()

    const fileRef = React.useRef(null)

    const fetchUri = async () => {
        setLoading(true)

        const { data: { signedUrl } } = await supabase.storage.from("folders").createSignedUrl(
            order.report_path,
            60
        )

        setLoading(false)

        window.open(signedUrl, '_blank')
    };

    const finish = async () => {
        setLoadingFinish(true)

        const formData = new FormData()

        if (report) {
            formData.append(`report`, report, report.name)
        }

        formData.append(`time`, activeTime)
        formData.append(`report_comment`, comment)

        const { error } = await fetch(`${APP_API_URL}/generation/validation/${order.pydio_id}`, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${session.access_token}`,
            },
            body: formData,
        }).then(res => res.json())

        setLoadingFinish(false)

        if (!error) {
            setOrder({
                ...order,
                finished_at: new Date()
            })
        } else {
            alert(error.message)
            return false;
        }
    }

    if (!order.report_pspdfkit) {
        return <OrderLoader icon={FileStackIcon} text="Prévualisation du rapport" />
    }

    if (isGeneratedOk === null) {
        return <div className="flex flex-col h-full flex-start gap-2">
            <Item onClick={() => !loadingFinish && setIsGeneratedOk(false)}>
                <PencilIcon className="h-4 w-4" />
                Je dois appliquer des corrections au document word
            </Item>
            <Item onClick={!loadingFinish && finish}>
                {(loadingFinish) && (
                    <LoaderCircleIcon className="h-4 w-4 animate-spin" />
                )}
                {!loadingFinish && <FileCheck2Icon className="h-4 w-4" />}
                Le rapport est correct
            </Item>
        </div >
    } else if (isGeneratedOk === false) {
        return <>
            <div className="flex items-center w-full gap-2">
                <Button className="flex-grow" variant={"outline"} onClick={fetchUri} disabled={!order.report_path || loading || loadingFinish}>
                    {(loading) && (
                        <LoaderCircleIcon className=" h-4 w-4 animate-spin" />
                    )}

                    {!loading && <DownloadIcon className=" h-4 w-4 " />}

                    Télécharger le docx
                </Button>

                <input accept=".docx" ref={fileRef} type="file" style={{ display: 'none' }} onChange={(event) => setReport(event.target.files[0])} />

                <Button className="flex-grow" variant={!report ? "default" : "secondary"} onClick={() => fileRef.current.click()} disabled={!order.report_path || loading || loadingFinish}>
                    {<UploadIcon className=" h-4 w-4 " />}

                    {!!report ? report.name : "Téléverser le docx corrigé"} <span className="text-red-500"> *</span>
                </Button>
            </div>

            <div className="flex flex-col w-full gap-2.5">
                <Label>
                    Quelles sont les corrections apportées au docx ?
                    <span className="text-red-500"> *</span>
                </Label>
                <AutoResizingTextarea rows={6} disabled={loadingFinish} onChange={(e) => setComment(e.target.value)} value={comment} />
            </div>
            <div className="flex items-center w-full gap-2">
                <Button variant="secondary" onClick={() => setIsGeneratedOk(null)}><ArrowLeftIcon /> Retour</Button>
                <Button className="flex-grow" onClick={finish} disabled={!order.report_path || loadingFinish || loading || !report || !comment}>
                    {(loadingFinish) && (
                        <LoaderCircleIcon className=" h-4 w-4 animate-spin" />
                    )}

                    {!loadingFinish && <ForwardIcon className=" h-4 w-4 " />}

                    Envoyer
                </Button>
            </div>
        </>
    }
}

export function ReportDocument() {
    const { order } = useOrder()

    if (!!order.report_pspdfkit) {
        return <PDF path={order.report_pspdfkit} />
    }

    return <Card className="h-full w-full flex-grow min-h-0 overflow-auto p-4 flex-col flex gap-2">
        <OrderLoader icon={FileStackIcon} text="Prévualisation du rapport" />
    </Card>
}

export function Item({ onClick, children }) {
    return (
        <ScrollArea>
            <div className="flex flex-col gap-2 pt-0">
                <button
                    className={cn(
                        "flex flex-col items-start gap-2 rounded-lg border p-3 text-left text-sm transition-all hover:bg-accent",
                    )}
                    onClick={onClick}
                >
                    <div className="flex items-center gap-4 font-semibold">
                        {children}
                    </div>
                </button>
            </div>
        </ScrollArea>
    )
}
